import { MemberUtils } from "@shared/member.utils";
import { PaymentUtils } from "@shared/payments.utils";
import { format } from "date-fns";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { BackButton } from "../components/common/BackButton";
import { Button } from "../components/common/Button";
import {
  PageTitle,
  PageTitleDetail,
  PageTitleSubtitle,
} from "../components/common/PageData";
import { useSeasonStore } from "../stores/season.store";
import { useProfileOwner, useUserStore } from "../stores/user.store";
import { getMemberIcon, getMemberTypeLabel } from "../styles/member";

const PageContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const MemberCard = styled.div`
  border: 1px solid #000000;
  border-radius: 8px;
  margin-bottom: 16px;
`;

const MemberContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
`;

const MemberInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const MemberName = styled.h3`
  font-size: 18px;
  margin: 0;
  font-weight: bold;
`;

const MemberTypeLabel = styled.p`
  font-size: 14px;
  margin: 0;
`;

const MembershipInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 16px;
  border-top: 1px solid #e0e0e0;
`;

const MembershipLabel = styled.span`
  font-size: 14px;
  font-weight: bold;
`;

const MembershipExpirationLabel = styled.span`
  font-size: 12px;
  color: #777777;
`;

const MembershipStatus = styled.span<{
  $status: "active" | "expired" | "pending";
}>`
  font-size: 14px;
  font-weight: bold;
  color: ${(props) =>
    props.$status === "active"
      ? "#4CAF50"
      : props.$status === "pending"
      ? "#FF9800"
      : "#F44336"};
`;

const ButtonContainer = styled.div`
  width: 100%;
`;

const ActionButton = styled.button<{
  $variant?: "primary" | "warning" | "danger";
}>`
  background-color: ${(props) =>
    props.$variant === "warning"
      ? "#FF9800"
      : props.$variant === "danger"
      ? "#F44336"
      : "#000000"};
  color: white;
  padding: 12px;
  border: none;
  width: 100%;
  font-weight: bold;
  cursor: pointer;
  border-radius: 0 0 8px 8px;

  &:hover {
    opacity: 0.9;
  }
`;

const TotalSection = styled.div`
  background-color: #e6f3ff;
  padding: 16px;
  margin: 20px 0;
  display: flex;
  justify-content: space-between;
  font-weight: bold;
  font-size: 18px;
  border-radius: 8px;
`;

const CalloutText = styled.p`
  font-size: 16px;
  margin: 20px 0;
`;

export const MembershipSubscription = () => {
  const navigate = useNavigate();
  const { profile } = useUserStore();
  const profileOwner = useProfileOwner();
  const { currentSeason } = useSeasonStore();
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);

  const paymentDetails = useMemo(() => {
    if (!currentSeason) {
      return null;
    }

    const members = profile?.members.filter((member) =>
      selectedMembers.includes(member.id),
    );
    return PaymentUtils.calculateSeasonPaymentDetails(
      members ?? [],
      currentSeason,
    );
  }, [selectedMembers, profile, currentSeason]);

  const formatRegistrationDate = (dateString?: string) => {
    if (!dateString) return "";
    return format(new Date(dateString), "dd/MM/yyyy");
  };

  const toggleMemberSelection = (memberId: string) => {
    if (selectedMembers.includes(memberId)) {
      setSelectedMembers(selectedMembers.filter((id) => id !== memberId));
    } else {
      setSelectedMembers([...selectedMembers, memberId]);
    }
  };

  const handleProceedToCheckout = () => {
    if (selectedMembers.length === 0) return;

    // Navigate to checkout with selected members
    navigate("/checkout", { state: { memberIds: selectedMembers } });
  };

  const showMembershipCard = (memberId: string) => {
    navigate(`/account/card?memberId=${memberId}`);
  };

  if (!profile || !profileOwner) {
    return <div>Loading...</div>;
  }

  if (!currentSeason) {
    return (
      <PageContainer>
        <BackButton />
        <PageTitle>TESSERAMENTO E RINNOVO</PageTitle>
        <PageTitleSubtitle>
          {profileOwner.first_name?.toUpperCase()}{" "}
          {profileOwner.last_name?.toUpperCase()}
        </PageTitleSubtitle>
        <PageTitleDetail>
          ISCRITTO IL {formatRegistrationDate(profileOwner.created_at)}
        </PageTitleDetail>
        <CalloutText>
          I tesseramenti sono temporaneamente chiusi. Saranno riaperti con
          l'inizio della nuova stagione.
        </CalloutText>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <BackButton />
      <PageTitle>TESSERAMENTO E RINNOVO</PageTitle>
      <PageTitleSubtitle>
        {profileOwner.first_name?.toUpperCase()}{" "}
        {profileOwner.last_name?.toUpperCase()}
      </PageTitleSubtitle>
      <PageTitleDetail>
        ISCRITTO IL {formatRegistrationDate(profileOwner.created_at)}
      </PageTitleDetail>

      <CalloutText>GESTISCI TUTTI I COMPONENTI DELLA TUA FAMIGLIA</CalloutText>

      {profile.members.map((member) => {
        const memberType = MemberUtils.getMemberType(
          member.date_of_birth,
          currentSeason
        );
        const currentYearSubscription = MemberUtils.getValidSubscription(
          member,
          currentSeason
        );
        const lastSubscription = MemberUtils.getLastSubscription(member);

        // Determine membership status
        let status: "active" | "expired" | "pending" = "expired";
        let statusText = "SCADUTA";
        let buttonText = "RINNOVA TESSERA";
        let buttonVariant: "primary" | "warning" | "danger" = "danger";

        if (currentYearSubscription) {
          status = "active";
          statusText = "ATTIVA";
          buttonText = "MOSTRA LA TESSERA";
          buttonVariant = "primary";
        } else if (selectedMembers.includes(member.id)) {
          status = "pending";
          statusText = "IN ATTESA";
          buttonText = "IN ATTESA DI PAGAMENTO";
          buttonVariant = "warning";
        }

        return (
          <MemberCard key={member.id}>
            <MemberContent>
              <MemberInfo>
                <MemberName>
                  {member.first_name} {member.last_name}
                </MemberName>
                <MemberTypeLabel>
                  {getMemberTypeLabel(memberType)}
                </MemberTypeLabel>
              </MemberInfo>
              <img src={getMemberIcon(memberType)}></img>
            </MemberContent>

            <MembershipInfo>
              <div style={{ display: "flex", flexDirection: "column" }}>
                <MembershipLabel>
                  {currentYearSubscription
                    ? `TESSERA ${currentSeason.name}`
                    : lastSubscription
                    ? `TESSERA ${lastSubscription.season_name}`
                    : `MAI TESSERATO`}
                </MembershipLabel>
                {currentYearSubscription && (
                  <MembershipExpirationLabel>
                    SCADENZA TESSERA{" "}
                    {format(new Date(currentSeason.end_date), "dd/MM/yyyy")}
                  </MembershipExpirationLabel>
                )}
              </div>
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                {!currentYearSubscription && (
                  <span>
                    {MemberUtils.getMemberPrice(member, currentSeason).toFixed(
                      2
                    )}{" "}
                    €
                  </span>
                )}
                <MembershipStatus $status={status}>
                  {statusText}
                </MembershipStatus>
              </div>
            </MembershipInfo>

            <ButtonContainer>
              {status === "active" ? (
                <ActionButton
                  onClick={() => showMembershipCard(member.id)}
                  $variant={buttonVariant}
                >
                  {buttonText}
                </ActionButton>
              ) : status === "pending" ? (
                <ActionButton
                  $variant={buttonVariant}
                  onClick={() => toggleMemberSelection(member.id)}
                >
                  {buttonText}
                </ActionButton>
              ) : (
                <ActionButton
                  $variant={buttonVariant}
                  onClick={() => toggleMemberSelection(member.id)}
                >
                  {buttonText}
                </ActionButton>
              )}
            </ButtonContainer>
          </MemberCard>
        );
      })}

      {selectedMembers.length > 0 && (
        <>
          <TotalSection>
            <span>TOTALE</span>
            <span>{paymentDetails?.total.toFixed(2)} €</span>
          </TotalSection>

          <Button fullWidth onClick={handleProceedToCheckout}>
            PROCEDI CON IL PAGAMENTO
          </Button>
        </>
      )}
    </PageContainer>
  );
};
